/**
 * Slice 3 — Operator Cockpit tiles embedded on the Dashboard.
 *
 * Three tiles rendered in a single row:
 *   1. System Health — rollup from /onboarding/diagnostics with top
 *      warnings/errors + a Fix deep-link.
 *   2. Recent Alerts — latest unread notifications with click-through.
 *   3. Active Campaigns — count-by-status card grid.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Activity, AlertTriangle, CheckCircle2, XCircle,
  Bell, LayoutDashboard, ChevronRight,
} from "lucide-react";
import { api } from "../../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";

function StatusPill({ status, small }) {
  const map = {
    ok:    "bg-emerald-100 text-emerald-800 border-emerald-300",
    warn:  "bg-amber-100 text-amber-800 border-amber-300",
    error: "bg-rose-100 text-rose-800 border-rose-300",
  };
  return (
    <Badge variant="outline"
              className={`font-mono ${small ? "text-[10px]" : "text-xs"} ${
                map[status] || "bg-slate-100 text-slate-700 border-slate-300"}`}>
      {status || "—"}
    </Badge>
  );
}

function SystemHealthTile() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/onboarding/diagnostics", { skipPermissionToast: true })
      .then((r) => setData(r.data))
      .catch(() => setData(null));
  }, []);
  const topFails = (data?.checks || [])
    .filter((c) => c.status === "error" || c.status === "warn")
    .slice(0, 3);
  return (
    <Card data-testid="cockpit-system-health-tile">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-2">
          <Activity className="size-3" />
          System Health
        </CardTitle>
        <StatusPill status={data?.status} />
      </CardHeader>
      <CardContent>
        {topFails.length === 0 && data && (
          <div className="text-xs text-emerald-700 dark:text-emerald-400 flex items-center gap-1">
            <CheckCircle2 className="size-3" />
            All checks pass.
          </div>
        )}
        <ul className="space-y-1">
          {topFails.map((c) => (
            <li key={c.name}
                   className="text-xs flex items-center justify-between gap-2"
                   data-testid={`cockpit-health-issue-${c.name}`}>
              <div className="flex items-center gap-2 min-w-0">
                {c.status === "error"
                  ? <XCircle className="size-3 text-rose-600 shrink-0" />
                  : <AlertTriangle className="size-3 text-amber-600 shrink-0" />}
                <span className="truncate">{c.label || c.name}</span>
              </div>
              {c.fix_action && (
                <Button
                  size="sm" variant="ghost" className="h-5 px-1 text-[10px]"
                  onClick={() => {
                    const fa = c.fix_action;
                    if (fa.kind === "open_settings" || fa.kind === "open_route")
                      nav(fa.route);
                  }}
                >Fix</Button>
              )}
            </li>
          ))}
        </ul>
        <Button variant="link" size="sm" className="h-5 p-0 text-[11px] mt-2"
                   onClick={() => nav("/app/settings?tab=diagnostics")}
                   data-testid="cockpit-health-view-all">
          View diagnostics <ChevronRight className="size-3" />
        </Button>
      </CardContent>
    </Card>
  );
}

function RecentAlertsTile() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  useEffect(() => {
    api.get("/notifications?unread_only=true&limit=5",
              { skipPermissionToast: true })
      .then((r) => setItems(r.data?.items || []))
      .catch(() => setItems([]));
  }, []);
  return (
    <Card data-testid="cockpit-recent-alerts-tile">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-2">
          <Bell className="size-3" />
          Recent Alerts
        </CardTitle>
        <Badge variant="outline" className="text-[10px]"
                  data-testid="cockpit-alerts-count">
          {items.length} unread
        </Badge>
      </CardHeader>
      <CardContent>
        {items.length === 0 && (
          <div className="text-xs text-muted-foreground">No unread alerts.</div>
        )}
        <ul className="space-y-1">
          {items.map((n) => (
            <li key={n.id}
                   onClick={() => n.deep_link_route && nav(n.deep_link_route)}
                   className="text-xs cursor-pointer hover:bg-muted/40 rounded px-1 py-0.5"
                   data-testid={`cockpit-alert-${n.id}`}>
              <div className="flex items-center gap-1">
                <StatusPill status={n.level === "error" ? "error"
                  : n.level === "warn" ? "warn" : "ok"} small />
                <span className="truncate flex-1">{n.title}</span>
              </div>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}

function ActiveCampaignsTile() {
  const nav = useNavigate();
  const [counts, setCounts] = useState({});
  useEffect(() => {
    api.get("/campaigns?limit=200", { skipPermissionToast: true })
      .then((r) => {
        const c = {};
        (r.data?.items || []).forEach((x) => {
          c[x.status] = (c[x.status] || 0) + 1;
        });
        setCounts(c);
      })
      .catch(() => setCounts({}));
  }, []);
  const buckets = [
    { key: "draft",       label: "Draft" },
    { key: "in_review",   label: "In review" },
    { key: "approved",    label: "Approved" },
    { key: "scheduled",   label: "Scheduled" },
    { key: "active",      label: "Active" },
    { key: "paused",      label: "Paused" },
  ];
  return (
    <Card data-testid="cockpit-active-campaigns-tile">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-2">
          <LayoutDashboard className="size-3" />
          Active Campaigns
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-3 gap-1">
          {buckets.map((b) => (
            <button
              key={b.key}
              onClick={() => nav(`/app/campaigns?status=${b.key}`)}
              data-testid={`cockpit-campaign-status-${b.key}`}
              className="border rounded p-2 text-left hover:bg-muted/40"
            >
              <div className="text-xl font-semibold tabular-nums">
                {counts[b.key] || 0}
              </div>
              <div className="text-[10px] text-muted-foreground">{b.label}</div>
            </button>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

export default function CockpitTiles() {
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4"
             data-testid="dashboard-cockpit-tiles">
      <SystemHealthTile />
      <RecentAlertsTile />
      <ActiveCampaignsTile />
    </div>
  );
}
